<div class="payout-tracker">
  <div class="revenue-dashboard-header">
    <div class="header-left">
      <div class="title-section">
        <div class="title-row">
          <h2 class="revenue-title">Payouts</h2>
        </div>
        <p class="revenue-description">
          Track Stripe payouts and upcoming transfers to your bank account.
        </p>
      </div>
    </div>
    <app-payments-toolbar-filters
      [showPeriodFilter]="false"
      [locationOptions]="locationOptions"
      [selectedLocationId]="selectedLocationId"
      [selectedLocationLabel]="selectedLocationLabel"
      (locationChange)="onLocationChange($event)"
      [statusOptions]="settlementStatuses"
      [selectedStatuses]="selectedStatuses"
      (selectedStatusesChange)="selectedStatuses = $event; onStatusChange()"
      statusSelectInputId="payoutTrackerStatusFilter"
      [exportLoading]="exportLoading"
      (exportClick)="onExport()"
    >
    </app-payments-toolbar-filters>
  </div>

  <div class="ledger-table-wrap">
    <div class="table-header-row">
      <h3 class="table-title">Payouts Tracker</h3>
    </div>

    <div class="table-scroll-wrapper" *ngIf="tableLoading">
      <app-loading-table-shimmer
        [columns]="shimmerColumns"
        [rows]="10"
        [showHeader]="false"
        className="scrollable"
      >
      </app-loading-table-shimmer>
    </div>

    <app-empty-result
      *ngIf="!tableLoading && rows.length === 0"
      image="/assets/images/empty_screen.png"
      title="No payouts found"
      description="Stripe payouts and upcoming transfers will appear here."
      [showAction]="false"
    >
    </app-empty-result>

    <div class="table-scroll-wrapper" *ngIf="!tableLoading && rows.length > 0">
      <table mat-table [dataSource]="dataSource" class="mat-mdc-table payments-table">
        <!-- Business Day -->
        <ng-container matColumnDef="businessDate">
          <th mat-header-cell *matHeaderCellDef style="min-width: 120px">Business Day</th>
          <td mat-cell *matCellDef="let row">{{ row.businessDate }}</td>
        </ng-container>

        <!-- Location -->
        <ng-container matColumnDef="location">
          <th mat-header-cell *matHeaderCellDef style="min-width: 150px">Location</th>
          <td mat-cell *matCellDef="let row">{{ row.locationName }}</td>
        </ng-container>

        <!-- Net Collected -->
        <ng-container matColumnDef="netCollected">
          <th mat-header-cell *matHeaderCellDef style="min-width: 130px">Net Collected</th>
          <td mat-cell *matCellDef="let row">
            <!-- A payout row covers several days and has no Net Collected. -->
            <span class="amount" *ngIf="row.netCollected !== null; else noNetCollected">
              {{ row.netCollected | currency: 'USD' : 'symbol' : '1.2-2' }}
            </span>
            <ng-template #noNetCollected>&mdash;</ng-template>
          </td>
        </ng-container>

        <!-- Refund -->
        <ng-container matColumnDef="refund">
          <th mat-header-cell *matHeaderCellDef style="min-width: 110px">Refund</th>
          <td mat-cell *matCellDef="let row">
            <span class="amount refund-amount" *ngIf="row.refund > 0; else noRefund">
              &minus;{{ row.refund | currency: 'USD' : 'symbol' : '1.2-2' }}
            </span>
            <ng-template #noRefund>&mdash;</ng-template>
          </td>
        </ng-container>

        <!-- Total Payout -->
        <ng-container matColumnDef="totalPayout">
          <th mat-header-cell *matHeaderCellDef style="min-width: 130px">Total Payout</th>
          <td mat-cell *matCellDef="let row">
            <!-- Refunds beyond the day's money: negative, taken from the next payout. -->
            <span class="amount refund-amount" *ngIf="row.totalPayout < 0; else payoutAmount">
              &minus;{{ -row.totalPayout | currency: 'USD' : 'symbol' : '1.2-2' }}
            </span>
            <ng-template #payoutAmount>
              <span class="amount">
                {{ row.totalPayout | currency: 'USD' : 'symbol' : '1.2-2' }}
              </span>
            </ng-template>
          </td>
        </ng-container>

        <!-- Bank -->
        <ng-container matColumnDef="bank">
          <th mat-header-cell *matHeaderCellDef style="min-width: 180px">Bank</th>
          <td mat-cell *matCellDef="let row">
            <div class="bank-cell" *ngIf="row.bankLast4; else noBank">
              <span class="bank-name" *ngIf="row.bankName">{{ row.bankName }}</span>
              <span class="bank-last4">&bull;&bull;&bull;&bull; {{ row.bankLast4 }}</span>
            </div>
            <ng-template #noBank>&mdash;</ng-template>
          </td>
        </ng-container>

        <!-- Status & Arrival -->
        <ng-container matColumnDef="statusArrival">
          <th mat-header-cell *matHeaderCellDef style="min-width: 180px">Status &amp; Arrive by</th>
          <td mat-cell *matCellDef="let row">
            <div class="status-cell">
              <span
                class="settlement-status"
                [ngClass]="row.statusClass"
                [class.has-hint]="!!row.statusHint"
                [matTooltip]="row.statusHint"
                matTooltipPosition="above"
                matTooltipClass="payout-hint-tooltip"
              >
                {{ row.statusLabel }}
              </span>
              <span class="arrive-by" *ngIf="row.arriveBy !== '—'">{{ row.arriveBy }}</span>
              <!-- CWS-1615: a day paid by more than one payout lists each of them. -->
              <span class="arrive-by payout-line" *ngFor="let line of row.payoutLines">{{
                line
              }}</span>
              <!-- Money moved between days, so Total Payout ≠ Net Collected − Refund. -->
              <span class="arrive-by" *ngFor="let line of row.movedMoneyLines">{{ line }}</span>
            </div>
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
        <!-- CWS-729: "Switched to manual payouts" divider, full width -->
        <ng-container matColumnDef="switchDivider">
          <td
            mat-cell
            *matCellDef="let row"
            class="switch-divider-cell"
            [attr.colspan]="displayedColumns.length"
          >
            <span class="switch-divider">
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <path
                  d="M4 7h11m0 0-3-3m3 3-3 3M20 17H9m0 0 3-3m-3 3 3 3"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              {{ row.label }}
            </span>
          </td>
        </ng-container>
        <tr
          mat-row
          *matRowDef="let r; columns: ['switchDivider']; when: isSwitchDivider"
          class="switch-divider-row"
        ></tr>
        <tr mat-row *matRowDef="let r; columns: displayedColumns"></tr>
      </table>
    </div>

    <app-pagination-bar
      *ngIf="paginationData.totalPages > 0"
      [paginationData]="paginationData"
      [itemsPerPageOptions]="[10, 25, 50, 100]"
      (pageChange)="onPageChange($event)"
      (itemsPerPageChange)="onItemsPerPageChange($event)"
    >
    </app-pagination-bar>
  </div>
</div>
